import React, { useCallback } from 'react';
import * as coreBi from '#packages/coreBi';
import {
  Composites,
  DropDown,
  DropDownOption,
  TextLabel,
} from '@wix/wix-base-ui';
import { BLEND_MODES } from '@wix/wow-utils';
import type {
  BlendMode,
  StyleDataItem,
  StyleRef,
} from '@wix/document-services-types';
import type { SendBiFunction } from 'types/bi';

interface Props {
  paramKey: string;
  styleDataItem: StyleDataItem;
  forceUpdateParentStyle: FunctionFixMe;
  sendBI: SendBiFunction;
  sendSiteSegmentBi: (biValues: {
    field_value: BlendMode;
    mouse_state: 'scroll' | 'regular';
    tab_name: string;
    field_name: string;
  }) => void;
}

const APPLY_BLEND_MODE = 'applyBlendMode';

function getLabelKey(word: BlendMode) {
  const key = word.charAt(0).toUpperCase() + word.slice(1);
  return `CustomDesign-Fill-BlendModeDropdown-${key}`;
}

const AdvancedStyleBlendModesControl: React.FC<Props> = ({
  styleDataItem,
  paramKey,
  forceUpdateParentStyle,
  sendSiteSegmentBi,
  sendBI,
}) => {
  const sendBi = useCallback(
    (biValues: Record<string, string>) => {
      sendBI(coreBi.events.designPanel.BOX_DESIGN_SETTINGS, {
        ...biValues,
        panel_name: 'advancedStyle',
      });
    },
    [sendBI],
  );

  const valueInData: string =
    (styleDataItem as StyleRef).style.properties[paramKey] || 'normal';

  const onBlendModeChange = useCallback(
    (value: BlendMode) => {
      const param = paramKey;
      (styleDataItem as StyleRef).style.properties[param] = value;
      forceUpdateParentStyle(styleDataItem, param, value);

      sendSiteSegmentBi?.({
        field_name: APPLY_BLEND_MODE,
        field_value: value,
        mouse_state: 'regular',
        tab_name: 'blend_effects',
      });

      sendBi({
        field_name: APPLY_BLEND_MODE,
        field_value: value,
      });
    },
    [
      paramKey,
      styleDataItem,
      forceUpdateParentStyle,
      sendSiteSegmentBi,
      sendBi,
    ],
  );

  return (
    <Composites.DropDownLabeled>
      <TextLabel
        value="CustomDesign-Fill-BlendMode-Label"
        shouldTranslate={true}
      />
      <DropDown onChange={onBlendModeChange} value={valueInData}>
        {BLEND_MODES.map((blendMode: BlendMode) => (
          <DropDownOption
            key={blendMode}
            value={blendMode}
            label={getLabelKey(blendMode)}
            shouldTranslate={true}
          />
        ))}
      </DropDown>
    </Composites.DropDownLabeled>
  );
};

AdvancedStyleBlendModesControl.displayName = 'advancedStyleBlendModesControl';

export default AdvancedStyleBlendModesControl;
